import React, { useState, useEffect, useRef, useMemo } from "react"
import {
  CalendarIcon,
  XMarkIcon,
  ChevronDownIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline"
import Typography from "aldoo-ra/Typography"
import DatePicker from "aldoo-ra/DatePicker"
import VariantReader from "aldoo-ra/VariantReader"
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences"
import { twMerge } from "tailwind-merge"
import moment from "moment"
import { isPeriodMode, presetRange, shiftRange, startsInFuture, formatPeriod } from "./periods"
import { WeekPanel, MonthPanel, QuarterPanel, YearPanel } from "./PeriodPanels"

// the tabs on top of the panel, in this order; "range" is the presets and the custom range
const ALL_MODES = ["range", "week", "month", "quarter", "year"]
const MODE_LABELS = { range: "Range", week: "Week", month: "Month", quarter: "Quarter", year: "Year" }

/**
 * DateFilterDropdown component for filtering data by date ranges
 * @param {Object} props - Component props
 * @param {Function} props.onChange - Callback function when date filter changes
 * @param {Object} props.value - Current filter value (controlled component)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disableLifetime - Option to disable lifetime filter
 * @param {Array} props.customPresets - Custom preset options
 * @param {string} props.variant - UI variant to use
 * @param {Object} props.styleOverrides - Override styles for the component
 * @param {string} props.label - Label for the dropdown
 * @param {string} props.defaultPreset - Preset a fresh dropdown opens on
 * @param {boolean} props.showArrows - Chevrons beside the button stepping to the
 *   range before or after: a week, month, quarter or year by its unit, any other
 *   range by its own number of days (off by default)
 * @param {Array} props.modes - The tabs on top of the panel, any of "range",
 *   "week", "month", "quarter", "year" (all by default; ["range"] for the presets alone)
 * @param {number} props.minYear - The oldest year the Year tab lists
 * @param {Function} props.t - Translates the control's own words (Range, Week, …)
 * @param {string} props.locale - moment locale for the day and month names
 * @returns {JSX.Element} The DateFilterDropdown component
 */
const DateFilterDropdown = ({
  onChange,
  value,
  className,
  disableLifetime = false,
  customPresets = null,
  variant = "default",
  styleOverrides = {},
  label = "Date Range",
  showLabel = true,
  highlightOnSelection = false,
  highlightResetValue = "lifetime",
  // Which preset a fresh dropdown opens on ("last24h", "last7d", "last30d",
  // "last90d", "last365d", "lifetime"). A remembered preference wins over it.
  defaultPreset = "lifetime",
  localUserPreferencesKey = null, // Key for storing user preferences
  showArrows = false,
  modes = ALL_MODES,
  minYear = moment().year() - 25,
  t = (text) => text,
  locale = undefined,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedPreset, setSelectedPreset] = useState(() => {
    // Try to load from user preferences first, then fall back to the default
    if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
      try {
        const savedPreset = LocalUserPreferences.get(`${localUserPreferencesKey}.preset`)
        if (savedPreset !== undefined && typeof savedPreset === 'string') {
          return savedPreset
        }
      } catch (error) {
        console.warn("Failed to load date filter preset preference:", error)
      }
    }
    return defaultPreset
  })
  const [customRange, setCustomRange] = useState(() => {
    // Try to load from user preferences first, then fall back to null
    if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
      try {
        const savedRange = LocalUserPreferences.get(`${localUserPreferencesKey}.customRange`)
        if (savedRange !== undefined && savedRange !== null) {
          return savedRange
        }
      } catch (error) {
        console.warn("Failed to load date filter custom range preference:", error)
      }
    }
    return null
  })
  const dropdownRef = useRef(null)
  // the tab the panel shows: the one the selection was made in, when it opens
  const [tab, setTab] = useState("range")
  const tabs = modes.filter((mode) => ALL_MODES.includes(mode))
  useEffect(() => {
    if (isOpen) setTab(isPeriodMode(selectedPreset) && tabs.includes(selectedPreset) ? selectedPreset : "range")
  }, [isOpen])

  // Get styles from variant system
  const variants = VariantReader.useVariant({
    componentName: "DateFilterDropdown",
    variant,
    styleOverrides,
  })

  // Memoize presets to ensure they don't change on every render
  const presets = useMemo(() => {
    const defaultPresets = [
      { id: "last24h", label: "Last 24 Hours", value: { days: 1 } },
      { id: "last7d", label: "Last 7 Days", value: { days: 7 } },
      { id: "last30d", label: "Last 30 Days", value: { days: 30 } },
      { id: "last90d", label: "Last 90 Days", value: { days: 90 } },
      { id: "last365d", label: "Last 365 Days", value: { days: 365 } },
      ...(disableLifetime
        ? []
        : [{ id: "lifetime", label: "Lifetime", value: null }]),
      { id: "custom", label: "Custom Range", value: "custom" },
    ]

    return customPresets || defaultPresets
  }, [customPresets, disableLifetime])

  // Sync with external value prop when it changes
  useEffect(() => {
    if (value) {
      if (value.type === "custom" && value.range) {
        // For custom date range — or a week, month, quarter or year, said by `mode`
        setCustomRange(value.range)
        setSelectedPreset(isPeriodMode(value.mode) ? value.mode : "custom")
      } else if (value.type === "preset") {
        // For preset values
        if (value.value === null) {
          // Lifetime
          setSelectedPreset("lifetime")
        } else {
          // Find matching preset by days
          const days = value.value?.days
          if (days) {
            const match = presets.find((p) => p.value?.days === days)
            if (match) {
              setSelectedPreset(match.id)
            }
          }
        }
      }
    }
  }, [value, presets])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Handle preset selection
  const handlePresetSelect = (presetId) => {
    setSelectedPreset(presetId)
    
    // Save to local user preferences if key is provided
    if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
      try {
        LocalUserPreferences.set(`${localUserPreferencesKey}.preset`, presetId)
      } catch (error) {
        console.warn("Failed to save date filter preset preference:", error)
      }
    }

    // Call onChange with appropriate value
    if (presetId === "custom") {
      // Only update if we have a valid custom range
      if (customRange) {
        onChange({ type: "custom", range: customRange })
      }
    } else {
      const preset = presets.find((p) => p.id === presetId)
      if (preset) {
        onChange({ type: "preset", value: preset.value })
      }
    }

    if (presetId !== "custom") {
      setIsOpen(false)
    }
  }

  // Handle custom range selection
  const handleCustomRangeSelect = (range) => {
    setCustomRange(range)
    
    // Save to local user preferences if key is provided
    if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
      try {
        LocalUserPreferences.set(`${localUserPreferencesKey}.customRange`, range)
      } catch (error) {
        console.warn("Failed to save date filter custom range preference:", error)
      }
    }

    // Call onChange with the custom date range
    if (range && range.startDate && range.endDate) {
      onChange({ type: "custom", range })
    }

    setSelectedPreset("custom")
  }

  // Remember a selection, as the presets and the custom range are remembered
  const remember = (presetId, range) => {
    if (!localUserPreferencesKey || !localUserPreferencesKey.trim()) return
    try {
      LocalUserPreferences.set(`${localUserPreferencesKey}.preset`, presetId)
      LocalUserPreferences.set(`${localUserPreferencesKey}.customRange`, range)
    } catch (error) {
      console.warn("Failed to save date filter preference:", error)
    }
  }

  // A week, month, quarter or year picked (or stepped to): a custom range that says what it is
  const selectRange = (presetId, range) => {
    setCustomRange(range)
    setSelectedPreset(presetId)
    remember(presetId, range)
    onChange(isPeriodMode(presetId) ? { type: "custom", range, mode: presetId } : { type: "custom", range })
  }

  const handlePeriodPick = (mode) => (range) => {
    selectRange(mode, range)
    setIsOpen(false)
  }

  // The range now selected, whatever it was picked as; null for Lifetime and
  // for a preset that is not a number of days
  const currentRange = useMemo(() => {
    if (selectedPreset === "custom" || isPeriodMode(selectedPreset)) return customRange?.startDate && customRange?.endDate ? customRange : null
    const days = presets.find((p) => p.id === selectedPreset)?.value?.days
    return days ? presetRange(days) : null
  }, [selectedPreset, customRange, presets])

  // A step from a preset leaves it: "Last 7 Days" a week back is seven days of their own
  const stepMode = isPeriodMode(selectedPreset) ? selectedPreset : "custom"
  const previousRange = currentRange ? shiftRange(currentRange, stepMode, -1) : null
  const nextRange = currentRange ? shiftRange(currentRange, stepMode, 1) : null
  const canStepNext = !!nextRange && !startsInFuture(nextRange)
  const step = (range) => range && selectRange(stepMode, range)

  // Get display label for current selection
  // Helper function to determine if selection should be highlighted
  const shouldHighlight = useMemo(() => {
    if (!highlightOnSelection) return false
    return selectedPreset !== highlightResetValue
  }, [highlightOnSelection, selectedPreset, highlightResetValue])

  // The widest the label ever gets is a custom range — two dates and the dash
  // between them. The control is given that width from the start, in
  // characters of the tabular digits it writes them in, so choosing a preset
  // and then a range does not resize the control (and what sits beside it).
  const labelWidth = `${"Sep 28 - Oct 4, 2026".length}ch`

  const getDisplayLabel = () => {
    if ((selectedPreset === "custom" || isPeriodMode(selectedPreset)) && customRange?.startDate && customRange?.endDate) {
      return formatPeriod(customRange, selectedPreset, locale)
    }

    const preset = presets.find((p) => p.id === selectedPreset)
    return preset ? preset.label : "Select Date Range"
  }

  return (
    <div className={twMerge(variants.root, className)} ref={dropdownRef}>
      <div className={variants.wrapper}>
        {showLabel && (
          <Typography variant="admin_label_sm_dim" className={variants.label}>
            {label}
          </Typography>
        )}

        <div className={showArrows ? variants.stepper : "contents"}>
        {showArrows && (
          <button
            type="button"
            className={variants.step_button}
            disabled={!previousRange}
            onClick={() => step(previousRange)}
            aria-label={t("Previous period")}
            data-testid="date-filter-prev"
          >
            <ChevronLeftIcon className={variants.step_icon} />
          </button>
        )}
        <div
          className={twMerge(
            variants.dropdown_button,
            showArrows && "flex-1 min-w-0",
            shouldHighlight && variants.highlight
          )}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex items-center">
            <CalendarIcon className={variants.dropdown_icon} />
            <Typography
              variant="admin_txt1"
              className={twMerge(
                variants.dropdown_text,
                "tabular-nums whitespace-nowrap overflow-hidden text-ellipsis"
              )}
              style={{ minWidth: labelWidth }}
            >
              {getDisplayLabel()}
            </Typography>
          </div>
          <ChevronDownIcon className={variants.chevron_icon} />
        </div>
        {showArrows && (
          <button
            type="button"
            className={variants.step_button}
            disabled={!canStepNext}
            onClick={() => step(nextRange)}
            aria-label={t("Next period")}
            data-testid="date-filter-next"
          >
            <ChevronRightIcon className={variants.step_icon} />
          </button>
        )}
        </div>
      </div>

      {isOpen && (
        <div className={twMerge(variants.popup, tabs.length > 1 && variants.popup_wide)}>
          {/* Range | Week | Month | Quarter | Year */}
          {tabs.length > 1 && (
            <div className={variants.tabs} role="tablist" data-testid="date-filter-tabs">
              {tabs.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  role="tab"
                  aria-selected={tab === mode}
                  className={twMerge(variants.tab?.base, tab === mode ? variants.tab?.active : variants.tab?.idle)}
                  onClick={() => setTab(mode)}
                  data-testid={`date-filter-tab-${mode}`}
                >
                  {t(MODE_LABELS[mode])}
                </button>
              ))}
            </div>
          )}
          {tab === "week" && <WeekPanel v={variants} value={selectedPreset === "week" ? customRange : null} onPick={handlePeriodPick("week")} locale={locale} />}
          {tab === "month" && <MonthPanel v={variants} value={selectedPreset === "month" ? customRange : null} onPick={handlePeriodPick("month")} locale={locale} />}
          {tab === "quarter" && <QuarterPanel v={variants} value={selectedPreset === "quarter" ? customRange : null} onPick={handlePeriodPick("quarter")} locale={locale} />}
          {tab === "year" && <YearPanel v={variants} value={selectedPreset === "year" ? customRange : null} onPick={handlePeriodPick("year")} minYear={minYear} />}
          {tab === "range" && (<>
          {/* Preset options */}
          <div className={variants.popup_scroll}>
            {presets.map((preset) => (
              <div
                key={preset.id}
                className={twMerge(
                  variants.preset_item.base,
                  selectedPreset === preset.id
                    ? variants.preset_item.active
                    : variants.preset_item.hover
                )}
                onClick={() => handlePresetSelect(preset.id)}
              >
                <Typography variant="admin_txt1" className="my-0.5">
                  {preset.label}
                </Typography>
                {selectedPreset === preset.id && (
                  <CheckIcon className={variants.preset_check_icon} />
                )}
              </div>
            ))}
          </div>

          {/* Custom date range picker */}
          {selectedPreset === "custom" && (
            <div className={variants.custom_section}>
              <DatePicker
                range={true}
                value={customRange}
                onChange={handleCustomRangeSelect}
                dateFormat="MM/DD/YYYY"
              />
            </div>
          )}
          </>)}
        </div>
      )}
    </div>
  )
}

export default DateFilterDropdown
